import React, { useLayoutEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import logo from "../../assets/logo.webp";
import "./Header.css";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [forceHamburger, setForceHamburger] = useState(false);
  const [hideTitle, setHideTitle] = useState(false);

  const navInnerRef = useRef(null);
  const linksRef = useRef(null);
  const titleRef = useRef(null);
  // Window width at which we decided to hide the title — used as a hysteresis
  // threshold so we don't loop between hide/show on tiny width fluctuations.
  const hideAtWidthRef = useRef(Infinity);

  const toggleMenu = () => setIsOpen((open) => !open);
  const closeMenu = () => setIsOpen(false);

  // Adaptive title: hide "All Star Xperience" text if it would be ellipsized,
  // keeping just the logo. Uses a hysteresis threshold so it doesn't loop —
  // once hidden, it only reappears when the window grows well past the width
  // that caused the clipping.
  useLayoutEffect(() => {
    const check = () => {
      const t = titleRef.current;
      if (!t) return;
      const winW = window.innerWidth;

      if (hideTitle) {
        // Only try to reveal once the window is meaningfully wider.
        if (winW > hideAtWidthRef.current + 40) {
          hideAtWidthRef.current = Infinity;
          setHideTitle(false);
        }
        return;
      }
      // Currently shown — check whether it's being clipped/ellipsized.
      // scrollWidth reflects the full text width, offsetWidth the rendered box.
      if (t.offsetWidth > 0 && t.scrollWidth > t.offsetWidth + 1) {
        hideAtWidthRef.current = winW;
        setHideTitle(true);
      }
    };
    check();
    const raf = requestAnimationFrame(check);
    const ro = new ResizeObserver(check);
    ro.observe(document.documentElement);
    if (navInnerRef.current) ro.observe(navInnerRef.current);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [hideTitle]);

  // Adaptive hamburger: if the nav-links row can't fit inline, collapse to
  // hamburger regardless of viewport width, rather than relying on a fixed
  // breakpoint that may not match the actual rendered link widths.
  useLayoutEffect(() => {
    const measure = () => {
      const inner = navInnerRef.current;
      const links = linksRef.current;
      if (!inner || !links) return;
      // If already in hamburger mode (mobile CSS), skip — the layout is stacked.
      if (getComputedStyle(links).position === "absolute") return;
      // Compare content width vs available width in the row.
      const overflow = links.scrollWidth > links.clientWidth + 2;
      setForceHamburger((prev) => (prev === overflow ? prev : overflow));
    };

    // Two-pass: first check current state, then re-check after a paint tick
    // in case force-hamburger changed the layout.
    measure();
    const rAF = requestAnimationFrame(measure);

    const ro = new ResizeObserver(measure);
    if (navInnerRef.current) ro.observe(navInnerRef.current);
    ro.observe(document.documentElement);

    return () => {
      cancelAnimationFrame(rAF);
      ro.disconnect();
    };
  }, [forceHamburger]);

  return (
    <nav className={`asx-nav ${forceHamburger ? "asx-nav-force-hamburger" : ""}`}>
      <div className="asx-container asx-nav-inner" ref={navInnerRef}>
        <div className="asx-brand">
          <img src={logo} alt="ASX" className="asx-logo" />
          <span
            ref={titleRef}
            className="asx-title"
            style={hideTitle ? { display: "none" } : undefined}
          >
            All Star Xperience
          </span>
        </div>

        <button
          className={`asx-nav-toggle ${isOpen ? "is-open" : ""}`}
          type="button"
          onClick={toggleMenu}
          aria-label="Toggle navigation"
          aria-expanded={isOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <div ref={linksRef} className={`asx-nav-links ${isOpen ? "is-open" : ""}`}>
          <NavLink to="/" end onClick={closeMenu}>Home</NavLink>
          <NavLink to="/leagues" onClick={closeMenu}>Leagues</NavLink>
          <NavLink to="/hall-of-fame" onClick={closeMenu}>Hall of Fame</NavLink>
          <NavLink to="/rules" onClick={closeMenu}>Rules & Regulations</NavLink>
          <NavLink to="/about" onClick={closeMenu}>About</NavLink>

          <NavLink to="/register" className="btn btn-primary" onClick={closeMenu}>
            Register Now
          </NavLink>
        </div>
      </div>
    </nav>
  );
}
